/** URL search params of the AI pages (kept apart from index.tsx so the admin console can parse them too). */

// The AI model pages (admins only, Admin → AI model). "paper" is the raw paper-trades view;
// "news" the language model behind the AI reading of the news (provider, budget, test).
export const AI_TABS = ["candidates", "models", "record", "paper", "news"] as const;
export type AiTab = (typeof AI_TABS)[number];
/** The paper-trades tab's views; "commodities" is the MCX paper record. */
export const PAPER_LEGS = ["futures", "options", "commodities"] as const;
export type PaperLeg = (typeof PAPER_LEGS)[number];

export interface AiSearch {
  tab: AiTab;
  /** Model version for the out-of-sample report (default: the active ensemble). */
  model?: string;
  /** Simulation run to show (default: the newest). */
  sim?: number;
  /** Paper trades: futures (default), option buying or commodities. */
  leg?: PaperLeg;
}

export function parseAiSearch(s: Record<string, unknown>): AiSearch {
  const sim = Number(s.sim);
  return {
    tab: AI_TABS.includes(s.tab as AiTab) ? (s.tab as AiTab) : "candidates",
    ...(typeof s.model === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(s.model) ? { model: s.model } : {}),
    ...(Number.isInteger(sim) && sim > 0 ? { sim } : {}),
    ...(PAPER_LEGS.includes(s.leg as PaperLeg) && s.leg !== "futures" ? { leg: s.leg as PaperLeg } : {}),
  };
}

/** The AI paper trades page's filters: what kind of trade, and when. */
export const TRADE_KINDS = ["all", "index", "options", "fno_stocks", "stocks", "commodities", "broadcast"] as const;
export type TradeKind = (typeof TRADE_KINDS)[number];
export const TRADE_SCOPES = ["today", "open", "history", "reports"] as const;
export type TradeScope = (typeof TRADE_SCOPES)[number];

/** The extra filters (owner, 30 Oct 2026): every one optional, all kept in the URL. */
export const LEGS = ["fut", "opt", "cash"] as const;
export const SIDES = ["buy", "sell"] as const;
export const OPTION_TYPES = ["CE", "PE"] as const;
export const RESULTS = ["win", "loss", "open", "waiting", "unfilled"] as const;
export const EXITS = ["target", "stop", "time"] as const;
export const VISIBILITY = ["users", "admin"] as const;
/** Who took the trade (owner, 3 Nov 2026): the opening-range rule or the AI Trader. */
export const TAKERS = ["rule", "ai"] as const;

export interface TradeFilters {
  /** Trade day range, IST (YYYY-MM-DD), both ends included. */
  from?: string;
  to?: string;
  leg?: (typeof LEGS)[number];
  /** Instruments, comma-separated ("NIFTY,GOLDM"). */
  sym?: string;
  side?: (typeof SIDES)[number];
  ot?: (typeof OPTION_TYPES)[number];
  res?: (typeof RESULTS)[number];
  exit?: (typeof EXITS)[number];
  vis?: (typeof VISIBILITY)[number];
  /** Taken by the rule or the AI Trader (every total on the page follows it). */
  by?: (typeof TAKERS)[number];
}

export interface TradesSearch extends TradeFilters {
  kind?: Exclude<TradeKind, "all">;
  scope?: Exclude<TradeScope, "today">;
  /** The "Check now" panel is open. */
  now?: 1;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const one = <T extends string>(list: readonly T[], v: unknown): T | undefined =>
  list.includes(v as T) ? (v as T) : undefined;

export function parseFilters(s: Record<string, unknown>): TradeFilters {
  const pick = {
    from: typeof s.from === "string" && DAY.test(s.from) ? s.from : undefined,
    to: typeof s.to === "string" && DAY.test(s.to) ? s.to : undefined,
    leg: one(LEGS, s.leg),
    sym: typeof s.sym === "string" && /^[A-Za-z0-9&*_,-]{1,300}$/.test(s.sym) ? s.sym.toUpperCase() : undefined,
    side: one(SIDES, s.side),
    ot: one(OPTION_TYPES, s.ot),
    res: one(RESULTS, s.res),
    exit: one(EXITS, s.exit),
    vis: one(VISIBILITY, s.vis),
    by: one(TAKERS, s.by),
  };
  return Object.fromEntries(Object.entries(pick).filter(([, v]) => v !== undefined)) as TradeFilters;
}

export function parseTradesSearch(s: Record<string, unknown>): TradesSearch {
  return {
    ...parseFilters(s),
    ...(TRADE_KINDS.includes(s.kind as TradeKind) && s.kind !== "all"
      ? { kind: s.kind as Exclude<TradeKind, "all"> }
      : {}),
    ...(TRADE_SCOPES.includes(s.scope as TradeScope) && s.scope !== "today"
      ? { scope: s.scope as Exclude<TradeScope, "today"> }
      : {}),
    ...(s.now === 1 || s.now === "1" || s.now === true ? { now: 1 as const } : {}),
  };
}
